import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../../../U3121";
import { useU3121Context } from "../../../../U3121Context";
import type { U3121Project, U3121Task, U3121TaskAssignment } from "../../../../U3121Service";

interface U3121ProjectTaskAssignmentBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121Project>;
  t: TFunction;
  /** Appelé après toute modification des tâches (checkbox, Tout sélectionner) pour recalculer les erreurs. */
  onValidateAfterTaskChange?: (project: U3121Project) => void;
}

/** Libellé linguistique de la tâche selon la langue de l'usager (pattern U3120 : champs Fr/En), avec repli. */
function getTaskLabel(task: U3121Task, language: string): string {
  if (language === "fr" && task.taskDescriptionFr?.trim()) return task.taskDescriptionFr.trim();
  if (language === "en" && task.taskDescriptionEn?.trim()) return task.taskDescriptionEn.trim();
  return task.taskDescription?.trim() || task.taskCode?.trim() || task.id;
}

/** Liste des ids cochés. Le backend peut renvoyer taskAssignments en string[] ou en { id: string }[]. */
function getSelectedTaskIdsFromProject(project: U3121Project | null | undefined): Set<string> {
  const raw = project?.taskAssignments;
  if (!raw?.length) return new Set();
  const ids = (raw as (string | U3121TaskAssignment)[]).map((item) => (typeof item === "string" ? item : item.id));
  return new Set(ids);
}

function U3121ProjectTaskAssignmentBody({ formStateHandlers, t, onValidateAfterTaskChange }: U3121ProjectTaskAssignmentBodyProps) {
  const { i18n } = useTranslation();
  const { initLU } = useU3121Context();
  const tasks = initLU.tasks ?? [];
  const language = i18n.language;
  const isEditMode = formStateHandlers.getFormState() !== U2000FormStateEnumState.READ;

  const project = formStateHandlers.getData();
  const selectedTaskIds = getSelectedTaskIdsFromProject(project);
  const errors = formStateHandlers.getErrors();
  const hasTaskAssignmentError = Boolean(errors["taskAssignments"]);
  const isSubmitted = formStateHandlers.isSubmitted();

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const taskIds = tasks.map((task) => task.id);
  const catalogIdSet = new Set(taskIds);
  const orphanSelectedIds = [...selectedTaskIds].filter((id) => !catalogIdSet.has(id));
  const allSelected = taskIds.length > 0 && taskIds.every((id) => selectedTaskIds.has(id));
  const someSelected = selectedTaskIds.size > 0 && selectedTaskIds.size < taskIds.length;

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someSelected;
  }, [someSelected]);

  const updateProjectTaskAssignments = (newIds: Set<string>) => {
    const data = formStateHandlers.getData();
    if (!data) return;
    const next = { ...data };
    next.taskAssignments = Array.from(newIds).map((id) => ({ id }));
    formStateHandlers.setData(next);
  };

  const runValidation = (nextProject: U3121Project) => {
    if (onValidateAfterTaskChange) onValidateAfterTaskChange(nextProject);
  };

  const handleSelectAllChange = () => {
    const clearingAll = allSelected || someSelected;
    const newIds = clearingAll ? new Set<string>() : new Set(taskIds);
    const nextProject: U3121Project = {
      ...(formStateHandlers.getData() ?? ({} as U3121Project)),
      taskAssignments: Array.from(newIds).map((id) => ({ id })),
    };
    updateProjectTaskAssignments(newIds);
    runValidation(nextProject);
  };

  const handleTaskChange = (taskId: string, checked: boolean) => {
    const next = new Set(selectedTaskIds);
    if (checked) next.add(taskId);
    else next.delete(taskId);
    const nextProject: U3121Project = {
      ...(formStateHandlers.getData() ?? ({} as U3121Project)),
      taskAssignments: Array.from(next).map((id) => ({ id })),
    };
    updateProjectTaskAssignments(next);
    runValidation(nextProject);
  };

  if (!isEditMode) {
    const selectedTasks = tasks.filter((task) => selectedTaskIds.has(task.id));
    if (selectedTasks.length === 0 && orphanSelectedIds.length === 0) {
      return <p className="text-gray-500">{t("msgNoTasksSelected", { ns: NS })}</p>;
    }
    return (
      <div className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6" role="table" aria-label={t("lblTaskAssignment", { ns: NS })}>
        {selectedTasks.map((task) => {
          const linguisticLabel = getTaskLabel(task, language);
          const code = task.taskCode?.trim();
          const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
          return (
            <label key={task.id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800">
              <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={displayLabel} />
              <span className="min-w-0 wrap-break-word">{displayLabel}</span>
            </label>
          );
        })}
        {orphanSelectedIds.map((id) => (
          <label key={id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-500 italic">
            <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={t("msgTaskInactiveAssigned", { ns: NS })} />
            <span className="min-w-0 wrap-break-word">{t("msgTaskInactiveAssigned", { ns: NS })}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <>
      {hasTaskAssignmentError && (isSubmitted || selectedTaskIds.size === 0) && (
        <div className="mb-4 flex items-start gap-1.5 rounded-md border-2 border-red-500 bg-red-50 p-3 animate-fade-in">
          <FaExclamationTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-red-600 leading-tight" role="alert" aria-live="polite">
            {errors["taskAssignments"]}
          </p>
        </div>
      )}
      {tasks.length === 0 ? (
        <p className="text-gray-500">{t("msgNoTasks", { ns: NS })}</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-gray-600">{t("msgTaskAssignmentsAdjustableHint", { ns: NS })}</p>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllChange}
                className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={t("lblSelectAllTasks", { ns: NS })}
                aria-checked={someSelected ? "mixed" : allSelected}
              />
              <span>{t("lblSelectAllTasks", { ns: NS })}</span>
            </label>
          </div>
          <div
            className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
            role="table"
            aria-label={t("lblTaskAssignment", { ns: NS })}
          >
            {tasks.map((task) => {
              const linguisticLabel = getTaskLabel(task, language);
              const code = task.taskCode?.trim();
              const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
              const checked = selectedTaskIds.has(task.id);
              return (
                <label key={task.id} role="cell" className="flex min-w-0 cursor-pointer items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    value={task.id}
                    checked={checked}
                    onChange={(e) => handleTaskChange(task.id, e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label={displayLabel}
                  />
                  <span className="min-w-0 wrap-break-word">{displayLabel}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

export default U3121ProjectTaskAssignmentBody;
